/**
 * Browser-side image decoding. Thin wrapper over `createImageBitmap` kept
 * separate from the pure sizing/validation logic so that logic stays testable
 * in a plain Node environment.
 */

import type { CropRect } from '$lib/types'
import { fitToLongSide, normalizeCrop } from './resize'

export interface DecodedImage {
  bitmap: ImageBitmap
  width: number
  height: number
}

export async function decodeImage(blob: Blob): Promise<DecodedImage> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(blob, { colorSpaceConversion: 'none' })
  } catch (cause) {
    throw new Error('The image could not be decoded. It may be corrupt or an unsupported format.', {
      cause,
    })
  }
  return { bitmap, width: bitmap.width, height: bitmap.height }
}

/**
 * Rasterizes a bitmap at the working resolution and returns its pixels.
 *
 * With a crop, only that region is drawn and it is what gets scaled to the
 * working resolution — so cropping to a detail buys resolution on it rather
 * than just discarding the surroundings.
 *
 * Uses an OffscreenCanvas so this works unchanged inside a worker.
 */
export function rasterize(
  bitmap: ImageBitmap,
  longSide: number,
  crop?: CropRect | null,
): ImageData {
  const region = normalizeCrop(crop, bitmap.width, bitmap.height)
  const source = region ?? { x: 0, y: 0, width: bitmap.width, height: bitmap.height }
  const { width, height } = fitToLongSide(source.width, source.height, longSide)
  const canvas = new OffscreenCanvas(width, height)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Could not acquire a 2D canvas context')

  // White ground: line art is commonly stored with transparency, and an
  // unpainted backdrop would otherwise binarize as ink.
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(bitmap, source.x, source.y, source.width, source.height, 0, 0, width, height)

  return ctx.getImageData(0, 0, width, height)
}
